'use client';

import {
  Button,
  CallToAction,
  Container,
  PageHeader,
  RelatedContent,
  ToolCard
} from '@paction-2.0/ui';
import type { ProviderMarker } from '@paction-2.0/ui';

import type { CalculatorDto } from '@/lib/api/tools';
import { BottomBanner } from '@/components/site/BottomBanner';

import { CalculatorIntro } from './components/CalculatorIntro';
import { CalculatorOutro } from './components/CalculatorOutro';
import CalculatorSections from './components/CalculatorSections';
import { PromoSection } from './components/PromoSection';
import ResultsPanel from './components/ResultsPanel';
import { RELATED_CONTENT, STEP_LINKS } from './data/costCalculatorContent';
import type { CostCalculatorBreed } from './data/costCalculatorBreeds';
import { RESULTS_ANCHOR_ID } from './hooks/useCostCalculatorClient';
import { useCostCalculatorExperience } from './hooks/useCostCalculatorExperience';
import styles from './costCalculatorClient.module.scss';

export type CostCalculatorClientProps = {
  calculator: CalculatorDto;
  initialBreed?: string;
  breeds?: CostCalculatorBreed[];
  providerMarkers?: ProviderMarker[];
};

export const CostCalculatorClient = ({
  calculator,
  initialBreed,
  breeds,
  providerMarkers
}: CostCalculatorClientProps) => {
  const {
    inputs,
    setInputs,
    selections,
    onSelect,
    resultsList,
    sectionTotals,
    defaultOutput,
    hasResults,
    resultsTitle,
    expandedSections,
    showCalculator,
    handleCopyShare,
    handleEmailSubmit,
    isSaving,
    handleClear,
    selectedBreed,
    sectionsAnchorRef,
    scrollToResults,
    handleGetStarted,
    handleToggleSection,
    handleExpandNext,
    handleCalculateAndScroll,
    resolvedBreeds,
    normalizedCalculatorInputs
  } = useCostCalculatorExperience({
    calculator,
    initialBreed,
    breeds
  });

  return (
    <div className={styles.calculator}>
      <PageHeader
        title={calculator.title ?? 'The Ultimate Dog Cost Calculator'}
        logoSrc="/icons/cost-calculator-icon.svg"
        logoAlt="cost calculator"
      />

      <Container maxWidth="default" padding="lg" className={styles.container}>
        <CalculatorIntro />

        <div className={styles.scrollButtonRow}>
          <Button
            type="button"
            className={styles.scrollButton}
            onClick={scrollToResults}
          >
            Calculate Now
          </Button>
        </div>

        <PromoSection providerMarkers={providerMarkers} />

        <div id={RESULTS_ANCHOR_ID} className={styles.resultsAnchor} />

        <ResultsPanel
          inputs={inputs}
          setInputs={setInputs}
          breeds={resolvedBreeds}
          calculatorInputs={normalizedCalculatorInputs}
          hasResults={hasResults}
          resultsList={resultsList}
          resultsTitle={resultsTitle}
          handleClear={handleClear}
          handleGetStarted={handleGetStarted}
          handleCopyShare={handleCopyShare}
          handleEmailSubmit={handleEmailSubmit}
          isSaving={isSaving}
        />

        {showCalculator ? (
          <div ref={sectionsAnchorRef} className={styles.sectionsAnchor}>
            <ToolCard as="section" className={styles.calculatorCard}>
              <CalculatorSections
                sections={calculator.sections}
                outputs={calculator.outputs}
                selections={selections}
                expandedSections={expandedSections}
                sectionTotals={sectionTotals}
                breed={selectedBreed}
                defaultOutput={defaultOutput}
                onToggleSection={handleToggleSection}
                onSelectOption={onSelect}
                onExpandNext={handleExpandNext}
                onRecalculate={
                  hasResults ? handleCalculateAndScroll : undefined
                }
                stepLinks={STEP_LINKS}
              />
            </ToolCard>

            <div className={styles.calculateBar}>
              <Button
                type="button"
                onClick={handleCalculateAndScroll}
                className={styles.calculateButton}
              >
                {hasResults ? 'Recalculate' : 'Calculate'}
              </Button>
            </div>
          </div>
        ) : null}

        <CalculatorOutro />

        <RelatedContent
          wide
          items={RELATED_CONTENT.map((item) => ({
            href: item.link,
            image: item.image,
            category: item.category,
            title: item.title,
            openInNewTab: item.external
          }))}
        />
      </Container>

      <Container
        maxWidth="default"
        padding="lg"
        className={styles.callToActionWrap}
      >
        <CallToAction className={styles.callToAction} />
      </Container>

      <BottomBanner
        message="Ready to find a pup?"
        buttonText="Search Reputable Breeders"
        href="/puppies"
      />
    </div>
  );
};

export default CostCalculatorClient;
